import { AccountPicker } from "@/components/accounting/account-picker";
import { MoneyDisplay } from "@/components/accounting/money-display";
import { ACCOUNT_TYPE_I18N } from "@/components/accounting/shared";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { Account } from "@/gen/omnios/v1/accounting_pb";
import {
	useCreateAccount,
	useGetBalance,
	useListAccounts,
	useUpdateAccount,
} from "@/hooks/use-accounting";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";

import type {
	ColDef,
	RowClassParams,
	SelectionChangedEvent,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Pencil, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/accounts")({
	component: ChartOfAccountsPage,
});

const ACCOUNT_TYPES = [
	{ value: 1, key: "type_asset" },
	{ value: 2, key: "type_liability" },
	{ value: 3, key: "type_equity" },
	{ value: 4, key: "type_revenue" },
	{ value: 5, key: "type_expense" },
] as const;

const codeDepth = (code: string) => code.split(".").length - 1;

function DetailPanel({
	account,
	onEdit,
}: { account: Account; onEdit: () => void }) {
	const { t } = useTranslation("accounting");
	const { data: balance } = useGetBalance(account.code);
	const typeKey = ACCOUNT_TYPE_I18N[account.accountType];

	return (
		<div className="w-72 border-l flex-shrink-0 overflow-y-auto text-xs">
			<div className="flex items-center justify-between px-3 py-2 border-b">
				<div className="min-w-0">
					<p className="font-mono text-sm font-bold truncate">{account.code}</p>
					<p className="text-muted-foreground truncate">{account.name}</p>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={onEdit}
					className="p-1 rounded hover:bg-accent flex-shrink-0"
				>
					<Pencil className="h-3.5 w-3.5" />
				</Button>
			</div>

			<div className="px-3 py-2 space-y-1.5">
				<div className="flex justify-between">
					<span className="text-muted-foreground">{t("account_type")}</span>
					<span>{typeKey ? t(typeKey) : "\u2014"}</span>
				</div>
				<div className="flex justify-between">
					<span className="text-muted-foreground">
						{t("account_normal_balance")}
					</span>
					<span>
						{account.normalBalance === 1
							? t("normal_debit")
							: t("normal_credit")}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-muted-foreground">{t("account_currency")}</span>
					<span>{account.currency || "RUB"}</span>
				</div>
				<div className="flex justify-between">
					<span className="text-muted-foreground">{t("account_status")}</span>
					<span>
						{account.active ? t("status_active") : t("status_inactive")}
					</span>
				</div>
			</div>

			<div className="px-3 py-2 border-t">
				<p className="text-muted-foreground mb-0.5">{t("account_balance")}</p>
				<MoneyDisplay
					amount={balance?.net ?? "0"}
					currency={account.currency || "RUB"}
					className="text-lg font-semibold"
					colorize
				/>
			</div>

			<div className="px-3 py-2 border-t space-y-1">
				<Link
					to="/accounting/accounts/$id"
					params={{ id: account.id }}
					className="block text-primary hover:underline"
				>
					{t("view_turnover")}
				</Link>
				<Link
					to="/accounting/journal"
					search={{ account: account.code }}
					className="block text-primary hover:underline"
				>
					{t("view_entries")}
				</Link>
			</div>
		</div>
	);
}

interface AccountFormData {
	code: string;
	name: string;
	accountType: string;
	normalBalance: string;
	parentId: string;
	currency: string;
	active: boolean;
	isGroup: boolean;
}

const EMPTY_FORM: AccountFormData = {
	code: "",
	name: "",
	accountType: "1",
	normalBalance: "1",
	parentId: "",
	currency: "RUB",
	active: true,
	isGroup: false,
};

function AccountDialog({
	open,
	onOpenChange,
	editAccount,
	accounts,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	editAccount?: Account;
	accounts: Account[];
}) {
	const { t } = useTranslation("accounting");
	const { t: tc } = useTranslation("common");
	const createMutation = useCreateAccount();
	const updateMutation = useUpdateAccount();
	const [form, setForm] = useState<AccountFormData>(EMPTY_FORM);

	const resetForm = useCallback((account?: Account) => {
		if (account) {
			setForm({
				code: account.code,
				name: account.name,
				accountType: String(account.accountType),
				normalBalance: String(account.normalBalance),
				parentId: account.parentId,
				currency: account.currency || "RUB",
				active: account.active,
				isGroup: account.isGroup,
			});
		} else {
			setForm(EMPTY_FORM);
		}
	}, []);

	const handleOpenChange = useCallback(
		(next: boolean) => {
			if (next) resetForm(editAccount);
			onOpenChange(next);
		},
		[editAccount, onOpenChange, resetForm],
	);

	const isEditing = !!editAccount;

	const handleSubmit = async () => {
		if (isEditing) {
			await updateMutation.mutateAsync({
				id: editAccount.id,
				name: form.name,
				currency: form.currency,
				parentId: form.parentId,
				active: form.active,
			});
		} else {
			await createMutation.mutateAsync({
				code: form.code,
				name: form.name,
				accountType: Number(form.accountType),
				normalBalance: Number(form.normalBalance),
				currency: form.currency,
				parentId: form.parentId,
				isGroup: form.isGroup,
			});
		}
		onOpenChange(false);
	};

	const isPending = createMutation.isPending || updateMutation.isPending;

	const pickerAccounts = useMemo(
		() =>
			accounts
				.filter((a) => a.isGroup && (!editAccount || a.id !== editAccount.id))
				.map((a) => ({
					id: a.id,
					code: a.code,
					name: a.name,
					accountType: a.accountType,
				})),
		[accounts, editAccount],
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{isEditing ? t("edit_account") : t("new_account")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label>{t("account_code")}</Label>
							<Input
								value={form.code}
								onChange={(e) =>
									setForm((f) => ({ ...f, code: e.target.value }))
								}
								disabled={isEditing}
								placeholder="01"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("account_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) =>
									setForm((f) => ({ ...f, name: e.target.value }))
								}
							/>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label>{t("account_type")}</Label>
							<Select
								value={form.accountType}
								onValueChange={(v) =>
									setForm((f) => ({ ...f, accountType: v }))
								}
								disabled={isEditing}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ACCOUNT_TYPES.map((at) => (
										<SelectItem key={at.value} value={String(at.value)}>
											{t(at.key)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("account_currency")}</Label>
							<Input
								value={form.currency}
								onChange={(e) =>
									setForm((f) => ({ ...f, currency: e.target.value }))
								}
							/>
						</div>
					</div>
					<div className="space-y-2">
						<Label>{t("account_parent")}</Label>
						<AccountPicker
							value={form.parentId}
							onChange={(id) => setForm((f) => ({ ...f, parentId: id }))}
							accounts={pickerAccounts}
						/>
					</div>
					<div className="flex items-center justify-between">
						<Label>{t("account_active")}</Label>
						<Switch
							checked={form.active}
							onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
						/>
					</div>
					{!isEditing && (
						<div className="flex items-center justify-between">
							<Label>{t("account_is_group")}</Label>
							<Switch
								checked={form.isGroup}
								onCheckedChange={(v) => setForm((f) => ({ ...f, isGroup: v }))}
							/>
						</div>
					)}
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{tc("cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isPending || !form.code || !form.name}
					>
						{isEditing ? tc("save") : tc("create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function ChartOfAccountsPage() {
	const { t } = useTranslation("accounting");
	useBreadcrumbs([{ label: t("nav_accounts") }]);
	const { onGridReady, onStateChanged } = useGridLayout("accounts");
	// Full list still needed for the account picker in the dialog
	const { data } = useListAccounts();
	const accounts = useMemo(() => data?.accounts ?? [], [data]);

	const [search, setSearch] = useState("");
	const [filterType, setFilterType] = useState("all");
	const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editAccount, setEditAccount] = useState<Account | undefined>(
		undefined,
	);

	const extraFilter = useMemo(() => {
		const f: Record<string, unknown> = {};
		if (filterType !== "all") f.accountType = Number(filterType);
		return f;
	}, [filterType]);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<Account>({
		colToField: {},
		fetchRows: async ({ pageSize, pageToken, sort, filter }) => {
			const resp = await accountingClient.listAccounts({
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				filter: filter as never,
				accountType: filterType !== "all" ? Number(filterType) : undefined,
			});
			return {
				rows: resp.accounts,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		search,
		extraFilter,
	});

	const onSearchChange = useCallback((value: string) => {
		setSearch(value);
	}, []);

	// Column defs
	const columnDefs = useMemo<ColDef<Account>[]>(
		() => [
			{
				field: "code",
				headerName: t("account_code"),
				width: 120,
				pinned: "left" as const,
				cellClass: "font-mono",
				cellStyle: (p) => {
					const depth = codeDepth(p.data?.code ?? "");
					return { paddingLeft: `${8 + depth * 16}px` };
				},
			},
			{
				field: "name",
				headerName: t("account_name"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "accountType",
				headerName: t("account_type"),
				width: 100,
				valueFormatter: (p) => {
					const key = ACCOUNT_TYPE_I18N[p.value as number];
					return key ? t(key) : "";
				},
			},
			{
				field: "normalBalance",
				headerName: t("account_normal_balance"),
				width: 80,
				filter: "agNumberColumnFilter",
				valueFormatter: (p) =>
					p.value === 1 ? t("normal_debit") : t("normal_credit"),
			},
			{
				field: "currency",
				headerName: t("account_currency"),
				width: 60,
				valueFormatter: (p) => p.value || "RUB",
			},
			{
				field: "active",
				headerName: t("account_status"),
				width: 80,
				filter: false,
				valueFormatter: (p) =>
					p.value ? t("status_active") : t("status_inactive"),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const getRowStyle = useCallback((params: RowClassParams<Account>) => {
		if (params.data?.isGroup) return { fontWeight: "600" as const };
		return undefined;
	}, []);

	const onSelectionChanged = useCallback(
		(e: SelectionChangedEvent<Account>) => {
			const selected = e.api.getSelectedRows();
			setSelectedAccount(selected.length > 0 ? selected[0] : null);
		},
		[],
	);

	const handleCreate = useCallback(() => {
		setEditAccount(undefined);
		setDialogOpen(true);
	}, []);

	const handleEdit = useCallback(() => {
		if (selectedAccount) {
			setEditAccount(selectedAccount);
			setDialogOpen(true);
		}
	}, [selectedAccount]);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: t("row_count", { count: totalRows }),
				align: "left" as const,
			},
			...(selectedAccount
				? [
						{
							id: "selected",
							content: `${selectedAccount.code} ${selectedAccount.name}`,
							align: "left" as const,
						},
					]
				: []),
		],
		[totalRows, selectedAccount, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* Filter strip */}
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Input
						type="text"
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder={t("search_accounts")}
						className="h-9"
					/>
				</div>

				<div className="flex items-center gap-0.5">
					<Button
						type="button"
						variant="ghost"
						onClick={() => setFilterType("all")}
						className={`px-1.5 text-xs leading-5 rounded-sm transition-colors ${
							filterType === "all"
								? "bg-accent text-accent-foreground font-medium"
								: "text-muted-foreground hover:text-foreground hover:bg-accent/50"
						}`}
					>
						{t("filter_all")}
					</Button>
					{ACCOUNT_TYPES.map((at) => (
						<Button
							key={at.value}
							type="button"
							variant="ghost"
							onClick={() => setFilterType(String(at.value))}
							className={`px-1.5 text-xs leading-5 rounded-sm transition-colors ${
								filterType === String(at.value)
									? "bg-accent text-accent-foreground font-medium"
									: "text-muted-foreground hover:text-foreground hover:bg-accent/50"
							}`}
						>
							{t(at.key)}
						</Button>
					))}
				</div>

				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={handleEdit}
					disabled={!selectedAccount}
				>
					{t("edit_account")}
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_export")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() =>
								gridRef.current?.api?.exportDataAsCsv({
									fileName: "accounts.csv",
								})
							}
						>
							CSV
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button size="sm" className="h-9" onClick={handleCreate}>
					<Plus className="h-4 w-4 mr-1" />
					{t("new_account")}
				</Button>
			</div>

			{/* Grid + detail panel */}
			<div className="flex flex-1 min-h-0">
				<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
					<AgGridReact<Account>
						ref={gridRef}
						{...gridProps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						rowSelection="single"
						onSelectionChanged={onSelectionChanged}
						onGridReady={onGridReady}
						onSortChanged={() => {
							onStateChanged();
							refreshGrid();
						}}
						onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						rowHeight={28}
						headerHeight={32}
						animateRows={false}
						getRowId={(p) => p.data.id}
						getRowStyle={getRowStyle}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_accounts_title", { defaultValue: "План счетов пуст" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_accounts_desc", { defaultValue: "Импортируйте план счетов из схемы или создайте счета вручную" })}</p></div>`}
					/>
				</div>

				{selectedAccount && (
					<DetailPanel account={selectedAccount} onEdit={handleEdit} />
				)}
			</div>

			<AccountDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				editAccount={editAccount}
				accounts={accounts}
			/>
		</div>
	);
}
